import { useEffect, useState } from "react"
import { IAnswer, IQuestion, PatientSurvey, TSurvey } from "../types/types"
import {
  checkValueIsPresentInAnswer,
  findAnswerValue,
  isAnswerChecked,
  moveElement2End
} from "../utils/surveys"
import {
  checkQuestionsConsistency,
  validateConsultationDate
} from "../utils/validations"

type PatientSurveyModalProps = {
  patientSurvey: PatientSurvey
  survey: TSurvey | undefined
  setAnswers: (answers: Array<IAnswer>) => void
  answers: Array<IAnswer>
  highlightUnansweredQuestions?: boolean
}

const questionLabelStyle = "text-sumo-500 mb-2"
const highlightedQuestionLabelStyle = `${questionLabelStyle} font-bold`

const Survey = (props: PatientSurveyModalProps) => {
  const { survey, answers, setAnswers, highlightUnansweredQuestions } = props
  const [error, setError] = useState<string | undefined>(undefined)

  const [consultationDateError, setConsultationDateError] = useState<
    string | undefined
  >(undefined)

  useEffect(() => {
    const questionIdsFiltered = survey?.questions
      .map((question) => {
        if (question.filtered) return question.id
        return undefined
      })
      .filter((_q) => _q !== undefined)

    if (questionIdsFiltered) {
      const _answers =
        answers?.filter(
          (answer) => !questionIdsFiltered.includes(answer.question_id)
        ) || []
      setAnswers([
        ..._answers,
        ...questionIdsFiltered.map((_qId) => ({
          question_id: _qId ?? 0,
          value: -1
        }))
      ])
    }
  }, [])

  const { questions } = checkQuestionsConsistency(survey?.questions, answers)

  const setConsistentAnswers = (answersToSet: Array<IAnswer>) => {
    const { answers: _answers } = checkQuestionsConsistency(
      survey?.questions,
      answersToSet
    )
    setAnswers(_answers ?? [])
  }

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value, id } = event.target
    const questionId = parseInt(id)
    const _answers =
      answers?.filter((answer) => answer.question_id !== questionId) || []

    const _question = survey?.questions.find(
      (question) => question.id === questionId
    )
    const selectedOption = _question?.options.find(
      (option) => option.label.toLocaleLowerCase() === value
    )

    setConsistentAnswers([
      ..._answers,
      { question_id: questionId, value: selectedOption?.value || 0 }
    ])
  }

  const handleNumberInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value, id } = event.target
    const questionId = parseInt(id)
    const _answers =
      answers?.filter((answer) => answer.question_id !== questionId) || []

    setConsistentAnswers([
      ..._answers,
      { question_id: questionId, value: parseInt(value) || 0 }
    ])
  }

  const handleTextInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value, id } = event.target
    const questionId = parseInt(id)
    const _answers =
      answers?.filter((answer) => answer.question_id !== questionId) || []

    setConsistentAnswers([
      ..._answers,
      { question_id: questionId, value: value || "" }
    ])
  }

  const handleSingleChoiceChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value, id } = event.target
    const _val = parseInt(value)
    const questionId = parseInt(id)
    const _answers =
      answers?.filter((answer) => answer.question_id !== questionId) || []

    setConsistentAnswers([
      ..._answers,
      { question_id: questionId, value: _val }
    ])
  }

  const handleMultipleChoiceChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value, id } = event.target
    const _val = parseInt(value)
    const questionId = parseInt(id)
    const _answers =
      answers?.filter((answer) => answer.question_id !== questionId) || []

    const _ans = answers?.find((answer) => answer.question_id === questionId)
    if (_ans && Array.isArray(_ans.value)) {
      const idx = _ans.value.indexOf(_val)
      let values =
        idx > -1
          ? _ans.value.filter((val) => val !== _val)
          : [..._ans.value, _val]

      setConsistentAnswers([
        ..._answers,
        {
          question_id: questionId,
          value: values
        }
      ])
    } else
      setConsistentAnswers([
        ..._answers,
        { question_id: questionId, value: [_val] }
      ])
  }

  const handleDateInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value, id } = event.target
    const error = validateConsultationDate(value)
    setConsultationDateError(error)

    const questionId = parseInt(id)
    const _answers =
      answers?.filter((answer) => answer.question_id !== questionId) || []

    setConsistentAnswers([
      ..._answers,
      { question_id: questionId, value: value || "" }
    ])
  }

  const getPlaceHolderText = (question: IQuestion) => "Entrez vos remarques"

  const renderQuestion = (question: IQuestion) => {
    const highLightQuestion =
      highlightUnansweredQuestions &&
      question.kind !== "TEXT" &&
      !answers.find((_ans) => _ans.question_id === question.id)

    const options =
      question.question_reference === "main_diagnostic"
        ? moveElement2End(question.options, "Autre")
        : question.options

    return (
      <div key={question.id} className="flex flex-col my-2">
        <label
          className={
            highLightQuestion
              ? highlightedQuestionLabelStyle
              : questionLabelStyle
          }
        >
          {highLightQuestion ? (
            <span className="text-sumo-800 text-base align-super">*</span>
          ) : null}
          {question.content}
        </label>

        {question.kind === "UNIQUE" && options.length <= 3 ? (
          <div className="flex flex-row">
            {options.map((option) => (
              <div className="flex flex-row mr-12">
                <input
                  type="checkbox"
                  name={question.question_reference}
                  id={question.id.toString()}
                  value={option.value}
                  onChange={handleSingleChoiceChange}
                  checked={isAnswerChecked(question.id, answers, option.value)}
                />
                <label className="ml-2">{option.label}</label>
              </div>
            ))}
          </div>
        ) : question.kind === "UNIQUE" && options.length > 3 ? (
          <select
            name={question.question_reference}
            id={question.id.toString()}
            className="mt-2 border-2 border-sumo-500 rounded-md p-2 w-full"
            onChange={handleSelectChange}
          >
            <option value="" selected disabled hidden>
              Sélectionnez une valeur
            </option>
            {options.map((option) => (
              <option
                className="text-sumo-900 text-sm"
                key={option.label}
                selected={isAnswerChecked(question.id, answers, option.value)}
              >
                {option.label.toLocaleLowerCase()}
              </option>
            ))}
          </select>
        ) : question.kind === "MULTIPLE" ? (
          <div className="columns-2">
            {options.map((option) => (
              <div className="mx-2 flex flex-row" key={option.label}>
                <input
                  type="checkbox"
                  name={question.question_reference}
                  id={question.id.toString()}
                  value={option.value}
                  onChange={handleMultipleChoiceChange}
                  checked={checkValueIsPresentInAnswer(
                    question.id,
                    answers,
                    option.value
                  )}
                />
                <label className="ml-3">{option.label}</label>
                <br />
              </div>
            ))}
          </div>
        ) : question.kind === "NUMBER" ? (
          <>
            <div className="flex-col">
              <input
                type="number"
                id={question.id.toString()}
                className="mt-2 border-2 border-sumo-500 rounded-md py-2 w-12 justify-end text-end"
                onChange={handleNumberInputChange}
                placeholder="0"
                value={findAnswerValue(question.id, answers)}
              />
            </div>
            {error && (
              <label className="text-sumo-800 text-sm mt-2">{error}</label>
            )}
          </>
        ) : question.kind === "TEXT" ? (
          <input
            type="text"
            id={question.id.toString()}
            className="mt-2 border-2 border-sumo-500 rounded-md p-2 w-full"
            onChange={handleTextInputChange}
            placeholder={getPlaceHolderText(question)}
            value={findAnswerValue(question.id, answers)}
          />
        ) : question.kind === "DATE" ? (
          <>
            <input
              className="border-2 border-slate-300 rounded-xl w-72 py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none"
              id={question.id.toString()}
              type="datetime-local"
              step={300}
              onChange={handleDateInputChange}
            />
            {consultationDateError && (
              <div className="text-sumo-800 text-xs w-72 text-justify">
                {consultationDateError}
              </div>
            )}
          </>
        ) : null}
      </div>
    )
  }

  return (
    <div className="mt-4">
      <div className="text-sumo-500 font-semibold text-base">
        {survey?.content}
      </div>
      <div className="text-sumo-500 overflow-y-auto no-scrollbar">
        {questions?.filter((_q) => !_q.filtered).length
          ? questions?.map(
              (question) =>
                question && !question.filtered && renderQuestion(question)
            )
          : "Vous n'avez pas d'action pour ce patient, en cliquant sur envoyer le patient recevra l'email d'information."}
      </div>
    </div>
  )
}

export default Survey
